import React, { createContext, useCallback, useContext, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import { type BFFConsumerEnum, RequestStatus } from '../constants';

export type ProviderValue = {
	cache: Record<string, any>;
	cacheKey: string;
	consumer: BFFConsumerEnum | null;
	error?: Error | null;
	status: RequestStatus | null;
};

export const defaultProviderValue: ProviderValue = {
	cache: {},
	cacheKey: '',
	status: null,
	consumer: null,
};

type BFFContext = {
	currentLoadingRequests: string[];
	forceOriginUrl?: string;
	setValue: React.Dispatch<React.SetStateAction<ProviderValue>>;
	updateCurrentLoadingRequests: (name: string, status: RequestStatus) => void;
	value: ProviderValue;
};

export const BFFContext = createContext<BFFContext>({
	value: defaultProviderValue,
	currentLoadingRequests: [],
	setValue: () => {},
	updateCurrentLoadingRequests: () => {},
});

type BFFProviderProps = {
	children: React.ReactNode;
	consumer: BFFConsumerEnum;
};

export function useBFFContext() {
	return React.useContext(BFFContext);
}

export function BFFProvider({ consumer, children }: BFFProviderProps): React.JSX.Element {
	const [value, setValue] = React.useState<ProviderValue>({
		cache: {},
		cacheKey: consumer,
		consumer,
		status: RequestStatus.INIT,
	});
	const [currentLoadingRequests, setCurrentLoadingRequests] = React.useState<string[]>([]);

	const updateCurrentLoadingRequests = useCallback(
		(name: string, status: string) => {
			switch (status) {
				case RequestStatus.LOADING:
					setCurrentLoadingRequests((prev) => [...prev, name]);
					break;
				case RequestStatus.COMPLETE:
					setCurrentLoadingRequests((prev) => prev.filter((request) => request !== name));
					break;
				case RequestStatus.ERROR:
					setCurrentLoadingRequests([]);
					break;
			}
		},
		[setCurrentLoadingRequests],
	);

	const stableContextValue = useMemo(
		() => ({
			value,
			setValue,
			currentLoadingRequests,
			updateCurrentLoadingRequests,
		}),
		[currentLoadingRequests, updateCurrentLoadingRequests, value],
	);

	return (
		<BFFContext.Provider
			value={
				fg('navx-2972_fix_fhs_locking_problems')
					? stableContextValue
					: {
							value,
							setValue,
							currentLoadingRequests,
							updateCurrentLoadingRequests,
						}
			}
		>
			{children}
		</BFFContext.Provider>
	);
}

export interface User {
	account_id: string;
	email: string;
	name: string;
	picture: string;
}

export type UserContextProps = {
	user?: User;
};

const defaultUserControls: UserContextProps = {
	user: undefined,
};

export const UserContext = createContext<UserContextProps>(defaultUserControls);

export function useUserContext() {
	return useContext(UserContext);
}
